<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="800">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <div>
                    <h4>{{ company.company_name }}'s Locations</h4>
                    <v-btn class="me-3" color="black" size="small" @click="addLocationDialogSwitch = true">
                        Add Location
                    </v-btn>
                </div>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-form v-if="formData.name" class="mb-3" validate-on="input lazy" @submit.prevent="updateLocation">
                    <v-alert v-if="errors.length"
                             class="mb-3 bg-error"
                             closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12">
                            <h5>Edit Location</h5>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['name']" :rules="[vRules.required]" density="compact"
                                          label="Name"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['longitude']" :rules="[vRules.required,vRules.numeric]"
                                          density="compact"
                                          label="Longitude"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['latitude']" :rules="[vRules.required,vRules.numeric]"
                                          density="compact"
                                          label="Latitude"/>
                        </v-col>

                        <v-col class="py-0 text-right" cols="12">
                            <v-btn color="primary" size="small" type="submit">Update</v-btn>
                        </v-col>
                    </v-row>

                </v-form>

                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <thead>
                            <tr>
                                <th>Name</th>
                                <!--                                <th>Longitude</th>
                                                                <th>Latitude</th>-->
                                <th>Alerts</th>
                                <th>Created At</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="location in locations" v-if="!loading && locations" :key="location.id"
                                class="text-left">
                                <td>{{ location.name }}</td>
                                <!--                                <td>{{ location.longitude }}</td>
                                                                <td>{{ location.latitude }}</td>-->
                                <td>
                                    <v-chip :color="location.enter_or_exit ? 'primary' : undefined" size="x-small"
                                            variant="tonal">{{ placeAlertLabel(location.enter_or_exit) }}
                                    </v-chip>
                                </td>
                                <td>{{ formatDateTime(location.created_at) }}</td>
                                <td>
                                    <v-btn-group density="compact" divided variant="outlined">
                                        <v-btn class="text-dark" prepend-icon="mdi-plus"
                                               size="x-small" @click="zoneListing(location)">Zone
                                        </v-btn>

                                        <v-btn class="text-warning" prepend-icon="mdi-account-switch"
                                               size="x-small" @click="editLocation(location)">Edit
                                        </v-btn>
                                        <v-btn class="text-error" prepend-icon="mdi-account-multiple-minus"
                                               size="x-small" title="Remove User"
                                               @click="deleteLocation(location)">Remove
                                        </v-btn>
                                    </v-btn-group>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" colspan="5">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else colspan="7">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row v-if="totalPages > 1" justify="center">
                    <v-col cols="8">
                        <v-pagination
                            :length="totalPages"
                            class="my-4"
                            density="compact"
                            v-model="currentPage"
                            @update:model-value="fetchLocations"
                        ></v-pagination>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn size="small" type="button"
                       @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Create v-if="addLocationDialogSwitch" v-model="addLocationDialogSwitch" :company="company"
            @update-locations="updateLocations"/>
    <Edit v-if="EditLocationDialogSwitch" v-model="EditLocationDialogSwitch" :location="selectedLocation"
          @update-location="updateLocation"/>
    <Zones v-if="zonesDialogSwitch" v-model="zonesDialogSwitch" :location="selectedLocation"/>
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {placeAlertLabel} from "@/composables/placeAlerts";
import {ref, reactive, onMounted} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import Zones from "@/components/dialogs/companies/Zones/Index.vue"
import Edit from "@/components/dialogs/companies/Locations/Edit.vue";
import Create from "@/components/dialogs/companies/Locations/Create.vue";
import {formatDateTime} from "@/composables/useTimezone";

const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    company: Object,
});

const formData = reactive({
    name: null,
    longitude: null,
    latitude: null,
    location: null,
});
const emits = defineEmits(['update:modelValue']);
const addLocationDialogSwitch = ref(false);
const EditLocationDialogSwitch = ref(false);
const dialogSwitch = ref(props.modelValue);
const selectedLocation = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const locations = ref([]);
const loading = ref(true);
const errors = ref([]);

const zonesDialogSwitch = ref(false);

onMounted(() => {
    fetchLocations();
});

const fetchLocations = () => {
    loading.value = true;
    send('get', `companies/locations/${props.company.id}?page=${currentPage.value}`)
        .then((response) => {
            locations.value = response.data.data.data;
            totalPages.value = response.data.last_page;
            currentPage.value = response.data.current_page;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Locations could not be loaded', 'error')
        })
}

const updateLocations = (location) => {
    locations.value.unshift(location);
}
const editLocation = (location) => {
    selectedLocation.value = location;
    EditLocationDialogSwitch.value = true;
}
const updateLocation = (updatedLocation) => {
    locations.value.map((location, index) => {
        if (location.id === updatedLocation.id) {
            locations.value[index] = updatedLocation;
        }
    });
}
const deleteLocation = (location) => {
    if (confirm(`Are you sure to remove "${location.name}" from ${props.company.company_name}?`)) {
        send('delete', `companies/locations/${location.id}`)
            .then((response) => {
                if (response.data.success) {
                    locations.value.map((item, index) => {
                        if (item.id === location.id) {
                            locations.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${location.name}" user could not be deleted`, 'error')
            })
    }
}

// add zone show dialog
const zoneListing = (location) => {
    selectedLocation.value = location;
    zonesDialogSwitch.value = true;
}

</script>
